import React from "react";
import type { Offering } from "@/lib/types/database/offerings";
import type {
  TabConfig,
  TabContentProps,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import { OfferingDetailsTab } from "@/features/offerings/components/OfferingDetailsTab";
import { OfferingDeliveryKitTab } from "@/features/offerings/components/OfferingDeliveryKitTab";

// Generic resolution by `customComponent` key (NOT a hand-maintained switch) so
// a tab declared in OFFERING_TABS can never silently fall through.
const OFFERING_TAB_COMPONENTS: Record<
  string,
  React.ComponentType<TabContentProps<Offering>>
> = {
  OfferingDetailsTab: OfferingDetailsTab as React.ComponentType<TabContentProps<Offering>>,
  OfferingDeliveryKitTab: OfferingDeliveryKitTab as React.ComponentType<TabContentProps<Offering>>,
};

function resolveOfferingTabComponent(
  tab: TabConfig<Offering> | undefined
): React.ComponentType<TabContentProps<Offering>> {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && OFFERING_TAB_COMPONENTS[custom]) {
    return OFFERING_TAB_COMPONENTS[custom];
  }
  return OFFERING_TAB_COMPONENTS.OfferingDetailsTab;
}

export interface UseOfferingDetailModalTabsReturn {
  tabs: TabConfig<Offering>[];
  renderPrimaryArea: (activeTab: string, offering: Offering) => React.ReactNode;
  renderInfoPanel: (offering: Offering) => React.ReactNode;
}

const OFFERING_TABS: TabConfig<Offering>[] = [
  {
    id: "details",
    label: "Details",
    icon: "FileText",
    variant: "blue",
    content: { type: "custom", customComponent: "OfferingDetailsTab" },
    group: "core",
    priority: 1,
  },
  {
    id: "delivery-kit",
    label: "Delivery Kit",
    icon: "Wrench",
    variant: "blue",
    content: { type: "custom", customComponent: "OfferingDeliveryKitTab" },
    group: "core",
    priority: 2,
  },
];

export function useOfferingDetailModalTabs(): UseOfferingDetailModalTabsReturn {
  const renderPrimaryArea = (activeTab: string, offering: Offering): React.ReactNode => {
    const tabConfig = OFFERING_TABS.find((t) => t.id === activeTab) ?? OFFERING_TABS[0];
    const TabComponent = resolveOfferingTabComponent(tabConfig);
    return <TabComponent item={offering} tab={tabConfig} />;
  };

  /** Unused when `onInlineEdit` is set — the modal uses `InlineEditableInfoPanel` from table config. */
  const renderInfoPanel = (_offering: Offering): React.ReactNode => null;

  return {
    tabs: OFFERING_TABS,
    renderPrimaryArea,
    renderInfoPanel,
  };
}
